import { useEffect, useMemo, useState } from 'react'

import {
  ApiError,
  api,
  can,
  saveFile,
  shortDate,
  type DocumentRegister,
  type DocumentVocabulary,
  type RegisterDocument,
  type Vendor,
} from '../lib/api'
import { codeAsWords, statusWord } from '../lib/status'
import { ErrorBox, Loading } from '../components/DataTable'
import { FilterBar, type FilterGroup, type FilterValue } from '../components/FilterBar'
import { Status, Toggletip, type Tone } from '../components/kit'
import { LegalReviewPanel, outcomeStatus } from '../components/LegalReview'
import { VendorChoice } from '../components/VendorChoice'
import { LEGAL_REVIEW_KINDS } from '../lib/legal'
import '../styles/registers.css'

// Every document the tenant holds, in one list: the document repository.
//
// # WHAT THIS SCREEN IS FOR
//
// The product officer's review of 17 September asked for Documents to be a
// place where a file is found and downloaded, not another view of a vendor's
// profile. So each row is one filed document, the title downloads it, and the
// filters are the three a person reaches for: which vendor, what kind of
// document, and whether it is still in date. The vendor's name still opens the
// vendor, for everything else.
//
// # WHY NOTHING HERE IS COMPUTED
//
// The state and the days remaining are the API's answer, taken against one
// date for the whole page. Working either out in the browser would let a
// reader in another time zone see a state that disagrees with the number
// printed beside it, and would put the definition of "expiring" in two places.

// The words come from the one vocabulary (lib/status.ts): a document the
// supplier sent and nobody has decided is In review, wherever it is shown.
const STATUS_WORD: Record<string, string> = Object.fromEntries(
  ['VALID', 'EXPIRING', 'EXPIRED', 'NO_EXPIRY', 'AWAITING', 'DECLINED'].map((c) => [c, statusWord('document', c).word]),
)

// Loud is for the two somebody has to act on. The rest are states, not tasks.
const LOUD = new Set(['EXPIRED', 'AWAITING'])
const WARN = new Set(['EXPIRING'])

// The tally chips beside the filter are filters, so they stay chips. A row's
// state is a status, which is never clickable.
function tone(status: string): string {
  if (LOUD.has(status)) return ' warn'
  if (WARN.has(status)) return ' strong'
  return ''
}

const STATUS_TONE: Record<string, Tone> = {
  EXPIRED: 'crit',
  AWAITING: 'brand',
  EXPIRING: 'warn',
  VALID: 'plain',
  NO_EXPIRY: 'plain',
  DECLINED: 'neutral',
}

// The API says how many days, negative once it has passed. This says it in
// words, because a bare -84 beside a certificate is a number a reader has to
// stop and think about and "84 days ago" is not.
function whenWord(d: RegisterDocument): string {
  if (d.daysToExpiry == null) return d.expiresOn ? shortDate(d.expiresOn) : 'No expiry recorded'
  const n = d.daysToExpiry
  if (n < 0) return 'Expired ' + Math.abs(n) + (Math.abs(n) === 1 ? ' day ago' : ' days ago')
  if (n === 0) return 'Expires today'
  if (n === 1) return 'Expires tomorrow'
  return 'Expires in ' + n + ' days'
}

// The address carries the filter, so a card on Home and a link in a message
// open the same list, and so somebody can send a colleague the list they are
// looking at rather than describing it.
// legal=awaiting is legal counsel's queue (release 3.1): the agreements with no
// standing legal review, where its home tile lands.
const HASH_KEYS = ['status', 'kind', 'vendor', 'legal']

function filterFromHash(): FilterValue {
  const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
  const v: FilterValue = {}
  for (const k of HASH_KEYS) {
    const raw = q.get(k)
    if (raw) v[k] = raw.split(',').filter(Boolean)
  }
  return v
}

// Two filters are the same when they select the same values, whatever order
// the groups or the values arrived in. Without this the hash listener would
// set a fresh object on every navigation and refetch a list that had not
// changed.
function same(a: FilterValue, b: FilterValue): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const k of keys) {
    const x = [...(a[k] ?? [])].sort()
    const y = [...(b[k] ?? [])].sort()
    if (x.length !== y.length || x.some((v, i) => v !== y[i])) return false
  }
  return true
}

function vendorWord(v: Pick<Vendor, 'displayName' | 'legalName' | 'vendorCode'>): string {
  return (v.displayName || v.legalName) + ' (' + v.vendorCode + ')'
}

// What a refused download means, in words a person can act on. The API names
// the two cases where a record exists and its file does not.
function refusalWord(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'CONTENT_GONE') return 'The file was not kept'
    if (err.code === 'CONTENT_ALTERED') return 'The stored file no longer matches, so it is not served'
    if (err.code === 'NO_OBJECT_STORE') return 'Files are not stored on this deployment'
    return err.message
  }
  return String(err)
}

export function Documents() {
  const [vocab, setVocab] = useState<DocumentVocabulary | null>(null)
  const [reg, setReg] = useState<DocumentRegister | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [filter, setFilter] = useState<FilterValue>(filterFromHash)
  const [query, setQuery] = useState('')
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [saving, setSaving] = useState<string | null>(null)
  const [refused, setRefused] = useState<Record<string, string>>({})
  // The agreement whose legal review is open, and a count that reloads the
  // list when a review is recorded.
  const [reviewing, setReviewing] = useState<RegisterDocument | null>(null)
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    api.documentVocabulary().then(setVocab).catch(setError)
  }, [])

  // The vendor filter picks from the register by name. A failure leaves the
  // picker empty rather than failing the page: the list still works.
  useEffect(() => {
    if (!can('vendor.read')) return
    let live = true
    api
      .vendors({})
      .then((p) => {
        if (live) setVendors(p.vendors)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  // The header's search box narrows this list, the same box that narrows the
  // register and the portfolio.
  useEffect(() => {
    const on = (e: Event) => setQuery(String((e as CustomEvent).detail ?? ''))
    window.addEventListener('tprm:query', on)
    return () => window.removeEventListener('tprm:query', on)
  }, [])

  // Arriving here from a card that already carries a filter has to apply it.
  // The screen does not remount when only the query part of the address
  // changes. Own writes go through replaceState, which raises no event, so
  // this only ever fires for somebody else's navigation.
  useEffect(() => {
    const on = () => {
      if (!window.location.hash.startsWith('#/documents')) return
      const next = filterFromHash()
      setFilter((cur) => (same(cur, next) ? cur : next))
    }
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])

  useEffect(() => {
    let live = true
    setReg(null)
    api
      .documentRegister({
        status: filter.status,
        kind: filter.kind,
        vendorId: filter.vendor?.[0],
        legalReview: filter.legal?.[0],
        limit: 500,
      })
      .then((r) => {
        if (live) setReg(r)
      })
      .catch((e) => {
        if (live) setError(e)
      })
    return () => {
      live = false
    }
  }, [filter, reloads])

  const chosenVendor = filter.vendor?.[0]
  const groups: FilterGroup[] = useMemo(() => {
    if (!vocab) return []
    const out: FilterGroup[] = [
      {
        key: 'status',
        label: 'State',
        options: vocab.statuses.map((s) => ({ value: s, label: STATUS_WORD[s] ?? codeAsWords(s) })),
      },
      {
        key: 'kind',
        label: 'Kind',
        options: vocab.kinds.map((k) => ({ value: k.code, label: k.name })),
      },
      {
        key: 'legal',
        label: 'Legal review',
        options: [{ value: 'awaiting', label: 'Awaiting legal review' }],
      },
    ]
    // The chosen vendor is a group of one, so it shows as a chip that can be
    // taken off like any other. Choosing one is the search box below.
    if (chosenVendor) {
      const v = vendors.find((x) => x.id === chosenVendor)
      const row = reg?.documents.find((d) => d.vendorId === chosenVendor)
      const word = v ? vendorWord(v) : row ? row.vendorName || row.vendorCode : 'One vendor'
      out.push({ key: 'vendor', label: 'Vendor', options: [{ value: chosenVendor, label: word }] })
    }
    return out
  }, [vocab, chosenVendor, vendors, reg])

  function apply(v: FilterValue) {
    setFilter(v)
    const q = new URLSearchParams()
    for (const [k, xs] of Object.entries(v)) if (xs.length) q.set(k, xs.join(','))
    const s = q.toString()
    window.history.replaceState(window.history.state, '', '#/documents' + (s ? '?' + s : ''))
  }

  async function download(d: RegisterDocument) {
    setSaving(d.id)
    setRefused((r) => ({ ...r, [d.id]: '' }))
    try {
      saveFile(await api.documentFile(d.id))
    } catch (err) {
      setRefused((r) => ({ ...r, [d.id]: refusalWord(err) }))
    } finally {
      setSaving(null)
    }
  }

  if (error) return <ErrorBox error={error} what="the document repository" />

  const rows = (reg?.documents ?? []).filter((d) => {
    if (!query) return true
    const hay = (d.title + ' ' + (d.vendorName ?? '') + ' ' + d.vendorCode + ' ' + d.docType).toLowerCase()
    return hay.includes(query.toLowerCase())
  })

  const kindWord = (code: string) => vocab?.kinds.find((k) => k.code === code)?.name ?? code
  // A register import can file a certificate the source register claimed with
  // no file behind it. It is listed, and says so, rather than offering a
  // download that can only fail.
  const hasFile = (d: RegisterDocument) => (d.objectKey ?? '').trim() !== ''

  const picker = (
    <div className="reg-picker">
      <label htmlFor="doc-vendor">Vendor</label>
      {/* The box never holds the choice: a chosen vendor is the chip on the
          bar, where it is taken off like any other filter. */}
      {vendors.length > 0 ? (
        <VendorChoice
          bare
          browse
          id="doc-vendor"
          label="Vendor"
          value=""
          options={vendors}
          onChange={(id) => {
            if (id) apply({ ...filter, vendor: [id] })
          }}
        />
      ) : (
        <input id="doc-vendor" type="search" placeholder="No vendors to pick from" disabled />
      )}
    </div>
  )

  return (
    <div className="p1-page">
      <div className="p1-head">
        <div>
          <h1>Documents</h1>
        </div>
      </div>

      <FilterBar groups={groups} value={filter} onChange={apply} panelExtra={picker}>
        {reg && (
          <div className="doc-tallies">
            {Object.entries(reg.byStatus)
              .filter(([, n]) => n > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([s, n]) => (
                <button
                  type="button"
                  key={s}
                  className={'chip' + tone(s) + ((filter.status ?? []).includes(s) ? ' on' : '')}
                  onClick={() => apply({ ...filter, status: (filter.status ?? []).includes(s) ? [] : [s] })}
                >
                  {n} {STATUS_WORD[s] ?? codeAsWords(s)}
                </button>
              ))}
          </div>
        )}
      </FilterBar>

      {!reg && <Loading what="the documents" />}

      {reg && rows.length === 0 && (
        <div className="empty">
          <strong>Nothing matches</strong>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {reg.total === 0 && !filter.vendor?.length ? (
              <>
                No documents have been filed against any vendor yet{' '}
                <Toggletip label="Where documents are filed">They are filed on the vendor, under Documents.</Toggletip>
              </>
            ) : (
              'Every filter can be taken off from the chips above'
            )}
          </p>
        </div>
      )}

      {reg && rows.length > 0 && (
        <div className="panel panel-flush">
          <div className="table-wrap">
            <table className="table reg-table">
              <caption className="vh">Documents filed against vendors, with their kind, state and expiry.</caption>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Vendor</th>
                  <th>Kind</th>
                  <th>State</th>
                  <th>Runs out</th>
                  <th>Filed</th>
                  <th>
                    Legal review{' '}
                    <Toggletip label="What a legal review is">
                      Legal counsel's recorded opinion of an agreement (DPA, MSA, SOW, NDA), clause by clause. Other
                      documents are evidence and take none.
                    </Toggletip>
                  </th>
                  <th>
                    <span className="vh">File</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      {hasFile(d) ? (
                        <button
                          type="button"
                          className="linkish reg-doc"
                          onClick={() => void download(d)}
                          disabled={saving === d.id}
                          title={'Download ' + d.title}
                        >
                          {d.title}
                        </button>
                      ) : (
                        <strong>{d.title}</strong>
                      )}
                      {d.source === 'VENDOR' && (
                        <span className="chip" style={{ marginLeft: 6 }}>
                          Sent by the vendor
                        </span>
                      )}
                      {refused[d.id] && (
                        <span className="cell-sub reg-refused" role="alert">
                          {refused[d.id]}
                        </span>
                      )}
                    </td>
                    <td>
                      {can('vendor.read') ? (
                        <a href={'#/vendor/' + encodeURIComponent(d.vendorId)}>{d.vendorName || d.vendorCode}</a>
                      ) : (
                        d.vendorName || d.vendorCode
                      )}
                    </td>
                    <td>{kindWord(d.docType)}</td>
                    <td>
                      <Status tone={STATUS_TONE[d.status] ?? 'neutral'}>{STATUS_WORD[d.status] ?? codeAsWords(d.status)}</Status>
                    </td>
                    <td>{whenWord(d)}</td>
                    <td className="muted reg-date">{shortDate(d.createdAt)}</td>
                    <td>
                      {LEGAL_REVIEW_KINDS.includes(d.docType) ? (
                        <span className="chiprow">
                          {outcomeStatus(d.legalReview)}
                          <button type="button" className="linkish" onClick={() => setReviewing(d)} aria-label={'Legal review of ' + d.title}>
                            {can('dpa.review') ? 'Review' : 'Open'}
                          </button>
                        </span>
                      ) : (
                        <span className="muted">Not an agreement</span>
                      )}
                    </td>
                    <td className="reg-filecell">
                      {hasFile(d) ? (
                        <button
                          type="button"
                          className="reg-download"
                          data-action="tprm.documents.a01"
                          onClick={() => void download(d)}
                          disabled={saving === d.id}
                        >
                          {saving === d.id ? 'Downloading' : 'Download'}
                        </button>
                      ) : (
                        <span className="muted">
                          No file{' '}
                          <Toggletip hover label="Why there is no file">
                            Recorded from an imported register. The register said the vendor holds it; no file was
                            sent.
                          </Toggletip>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="summary-line reg-foot" role="status">
            <strong>{rows.length}</strong> of <strong>{reg.total}</strong> document{reg.total === 1 ? '' : 's'}
            {reg.omitted > 0 ? ', ' + reg.omitted + ' more are not on this page' : ''}
          </p>
        </div>
      )}

      {reviewing && (
        <LegalReviewPanel
          documentId={reviewing.id}
          title={reviewing.title}
          onClose={() => setReviewing(null)}
          onSaved={() => setReloads((n) => n + 1)}
        />
      )}
    </div>
  )
}
